import React, { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import { ILegalPersonType } from 'models/Ticket';
import CustomerService from 'services/customer.service';

import IconSuccess from 'img/success-img.svg';

import { Row, Column } from 'components/Layout/Grid';
import RadioInput from 'components/RadioInput';
import { Button } from 'components/Button';
import Input from 'components/Input';
import Form from 'components/Form';
import Text from 'components/Text';

import { formatDocument, DocumentDigits, DocumentStr } from 'utils/Document';
import { PhoneNumberOutput, PhoneNumberTest } from 'utils/PhoneNumber';
import { CepNumberOutput, ValidateCep } from 'utils/Cep';
import { ValidateEmail } from 'utils/Email';
export interface IFinalCustomer {
  documentNumber: string;
  companyName: string;
  phone: string;
  comercialEmail?: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

const AddNewCustomer: React.FC = () => {
  const slider = useSelector((state: RootState) => state.slider);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [registeredCustomer, setRegisteredCustomer] = useState({});
  const [legalPerson, setLegalPerson] = useState(ILegalPersonType.NATURAL_PERSON);
  const [newCustomerData, setNewCustomerData] = useState({
    documentNumber: '',
    stateRegistration: '',
    companyName: '',
    phone: '',
    comercialEmail: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    state: '',
    city: '',
  });

  const dispatch = useDispatch();
  const _customerService = new CustomerService();

  async function addCustomer() {
    setLoading(true);
    setErrorMessage('');

    if (!formatDocument(DocumentDigits(newCustomerData.documentNumber))) {
      setLoading(false);
      return setErrorMessage('CPF inválido');
    }

    if (newCustomerData.companyName.length < 3) {
      setLoading(false);
      return setErrorMessage('Nome deve ter ao menos 3 caracteres');
    }

    if (!PhoneNumberTest(PhoneNumberOutput(newCustomerData.phone))) {
      setLoading(false);
      return setErrorMessage('Digite um Telefone válido');
    }

    if (
      newCustomerData.comercialEmail !== '' &&
      !ValidateEmail(newCustomerData.comercialEmail)
    ) {
      setLoading(false);
      return setErrorMessage('Digite um E-mail válido');
    }

    if (!ValidateCep(newCustomerData.zipCode)) {
      setLoading(false);
      return setErrorMessage('CEP inválido');
    }

    if (newCustomerData.street.length < 3) {
      setLoading(false);
      return setErrorMessage('Endereço deve ter ao menos 3 caracteres');
    }

    if (newCustomerData.number.length < 1) {
      setLoading(false);
      return setErrorMessage('Número deve ter ao menos 1 caractere');
    }

    if (
      newCustomerData.complement !== '' &&
      newCustomerData.complement.length < 3
    ) {
      setLoading(false);
      return setErrorMessage('Complemento deve ter ao menos 3 caracteres');
    }

    if (newCustomerData.neighborhood.length < 3) {
      setLoading(false);
      return setErrorMessage('Bairro deve ter ao menos 3 caracteres');
    }

    if (newCustomerData.state.length < 2) {
      setLoading(false);
      return setErrorMessage('Estado deve ter ao menos 2 caracteres');
    }

    if (newCustomerData.city.length < 3) {
      setLoading(false);
      return setErrorMessage('Cidade deve ter ao menos 3 caracteres');
    }

    const data = {
      documentNumber: DocumentDigits(newCustomerData.documentNumber),
      companyName: newCustomerData.companyName,
      phone: newCustomerData.phone,
      comercialEmail: newCustomerData.comercialEmail,
      address: {
        zipCode: newCustomerData.zipCode,
        street: newCustomerData.street,
        number: newCustomerData.number,
        complement: newCustomerData.complement,
        neighborhood: newCustomerData.neighborhood,
        state: newCustomerData.state,
        city: newCustomerData.city,
        stateRegistration: newCustomerData.stateRegistration,
      },
      personType: legalPerson === ILegalPersonType.NATURAL_PERSON ? 'FISICA' : 'Juridica',
    };

    await _customerService
      .create(data)
      .then((result: any) => {
        if (result?.status === 200) {
          setRegisteredCustomer(result.data.data);
          return setSuccess(true);
        }
      })
      .catch((ex) => {
        if (ex.response.data.code === 'MESSAGE_ERROR') {
          setErrorMessage(ex.response.data.data);
        } else {
          setErrorMessage('Ocorreu um erro durante a requisição');
        }
        setLoading(false);
      });

    setLoading(false);
  }

  function onCloseSlider() {
    if (registeredCustomer !== '') {
      slider?.state?.setCustomerData(registeredCustomer);
    }

    dispatch({ type: 'CLOSE_SLIDER' });
    dispatch({ type: 'SHOW_TITLE' });
  }

  useEffect(() => {
    setNewCustomerData({
      ...newCustomerData,
      documentNumber: ''
    });
  }, [legalPerson]);

  const handleNewCustomerData = (target: HTMLInputElement) => {
    setNewCustomerData({
      ...newCustomerData,
      zipCode: target.value
    });
  };

  const customerType = (value: string) => {
    if (legalPerson === ILegalPersonType.NATURAL_PERSON)
      return value === 'document' ? 'CPF' : 'Nome';

    if (legalPerson === ILegalPersonType.JURIDICAL_PERSON)
      return value === 'document' ? 'CNPJ' : 'Razão Social';
  };

  return !success ? (
    <Form
      className="flex-1"
      id="add_new_customer_form"
      onValidate={() => addCustomer()}
    >
      <Column className="flex-1">
        <Row className="mt-3">
          <Column>
            <Text className="mb-2 color-blue-gray" size={12}>
              Tipo de Cliente
            </Text>
            <Row className="flex-md-column">
              <Row className="w-md-100 mt-md-2" style={{ width: 200 }}>
                <RadioInput
                  id="NEW_CUSTOMER_FISICAL_PERSON"
                  onClick={() =>
                    setLegalPerson(ILegalPersonType.NATURAL_PERSON)
                  }
                  checked={
                    legalPerson === ILegalPersonType.NATURAL_PERSON
                  }
                >
                  <Text className="color-blue-gray">CPF</Text>
                </RadioInput>
              </Row>

              <Row className="w-md-100 mt-md-2" style={{ width: 200 }}>
                <RadioInput
                  id="NEW_CUSTOMER_JURIDICAL_PERSON"
                  onClick={() =>
                    setLegalPerson(ILegalPersonType.JURIDICAL_PERSON)
                  }
                  checked={
                    legalPerson === ILegalPersonType.JURIDICAL_PERSON
                  }
                >
                  <Text className="color-blue-gray">CNPJ</Text>
                </RadioInput>
              </Row>
            </Row>
          </Column>
        </Row>

        <Row>
          <Column className="mt-3 w-100">
            <Text className="color-blue-gray mb-2">
              {customerType('document')}*
            </Text>
            <Input
              id={legalPerson === ILegalPersonType.NATURAL_PERSON ? 'cpf-new-customer' : 'cnpj-new-customer'}
              required
              onChange={(e: any) =>
                setNewCustomerData({
                  ...newCustomerData,
                  documentNumber: e.target.value,
                })
              }
              className="w-100"
              type="text"
              value={DocumentStr({
                type: legalPerson === ILegalPersonType.NATURAL_PERSON ? 'cpf' : 'cnpj',
                value: newCustomerData.documentNumber,
              })}
              placeholder={customerType('document')}
            />
          </Column>

          {legalPerson === ILegalPersonType.JURIDICAL_PERSON && (
            <Column className="mt-3 w-100 ml-4">
              <Text className="color-blue-gray mb-2">Inscrição Estadual*</Text>
              <Input
                id="state-registration-new-customer"
                required
                onChange={(e: any) =>
                  setNewCustomerData({
                    ...newCustomerData,
                    stateRegistration: e.target.value,
                  })
                }
                className="w-100"
                type="text"
                value={newCustomerData.stateRegistration}
                placeholder="Inscrição Estadual"
              />
            </Column>
          )}
        </Row>

        <Column className="mt-3 w-100">
          <Text className="color-blue-gray mb-2">
            {customerType('companyName')}*
          </Text>
          <Input
            id={legalPerson === ILegalPersonType.NATURAL_PERSON ? "name-input" : "corporate-name"}
            required
            onChange={(e: any) =>
              setNewCustomerData({
                ...newCustomerData,
                companyName: e.target.value,
              })
            }
            className="w-100"
            type="text"
            value={newCustomerData.companyName}
            placeholder="Nome"
          />
        </Column>

        <Row>
          <Column className="mt-3 w-30">
            <Text className="color-blue-gray mb-2">Telefone*</Text>
            <Input
              id="telephone-new-customer"
              required
              onChange={(e: any) =>
                setNewCustomerData({ ...newCustomerData, phone: e.target.value })
              }
              className="w-100"
              type="text"
              value={PhoneNumberOutput(newCustomerData.phone)}
              placeholder="Telefone"
            />
          </Column>

          <Column className="mt-3 w-70 ml-4">
            <Text className="color-blue-gray mb-2">E-mail</Text>
            <Input
              id="email-new-customer"
              onChange={(e: any) =>
                setNewCustomerData({
                  ...newCustomerData,
                  comercialEmail: e.target.value,
                })
              }
              className="w-100"
              type="email"
              value={newCustomerData.comercialEmail}
              placeholder="E-mail"
            />
          </Column>
        </Row>

        <Row>
          <Column className="mt-3 w-30">
            <Text className="color-blue-gray mb-2">CEP*</Text>
            <Input
              id="cep-new-customer"
              required
              onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                handleNewCustomerData(target)
              }
              onBlur={({ target }: ChangeEvent<HTMLInputElement>) =>
                handleNewCustomerData(target)
              }
              className="w-100"
              type="text"
              value={CepNumberOutput(newCustomerData.zipCode)}
              placeholder="CEP"
            />
          </Column>

          <Column className="mt-3 w-70 ml-4">
            <Text className="color-blue-gray mb-2">Endereço*</Text>
            <Input
              id="address-new-customer"
              required
              onChange={(e: any) =>
                setNewCustomerData({ ...newCustomerData, street: e.target.value })
              }
              className="w-100"
              type="text"
              value={newCustomerData.street}
              placeholder="Endereço"
            />
          </Column>
        </Row>

        <Row className="mt-3">
          <Column className="w-30">
            <Text className="color-blue-gray mb-2">Número*</Text>
            <Input
              id="number-address-new-customer"
              required
              onChange={(e: any) =>
                setNewCustomerData({
                  ...newCustomerData,
                  number: e.target.value,
                })
              }
              className="w-100"
              type="text"
              value={newCustomerData.number}
              placeholder="Número"
            />
          </Column>

          <Column className="w-70 ml-4">
            <Text className="color-blue-gray mb-2">Complemento</Text>
            <Input
              id="complement-new-customer"
              onChange={(e: any) =>
                setNewCustomerData({
                  ...newCustomerData,
                  complement: e.target.value,
                })
              }
              className="w-100"
              type="text"
              value={newCustomerData.complement}
              placeholder="Complemento"
            />
          </Column>
        </Row>

        <Row className="mt-3">
          <Column className="flex-1">
            <Text className="color-blue-gray mb-2">Bairro*</Text>
            <Input
              id="neighborhood-new-customer"
              required
              onChange={(e: any) =>
                setNewCustomerData({
                  ...newCustomerData,
                  neighborhood: e.target.value,
                })
              }
              className="w-100"
              type="text"
              value={newCustomerData.neighborhood}
              placeholder="Bairro"
            />
          </Column>

          <Column className="flex-1 ml-4">
            <Text className="color-blue-gray mb-2">Estado*</Text>
            <Input
              id="state-new-customer"
              required
              onChange={(e: any) =>
                setNewCustomerData({
                  ...newCustomerData,
                  state: e.target.value,
                })
              }
              className="w-100"
              type="text"
              value={newCustomerData.state}
              placeholder="Estado"
            />
          </Column>

          <Column className="flex-1 ml-4">
            <Text className="color-blue-gray mb-2">Cidade*</Text>
            <Input
              id="city-new-customer"
              required
              onChange={(e: any) =>
                setNewCustomerData({
                  ...newCustomerData,
                  city: e.target.value,
                })
              }
              className="w-100"
              type="text"
              value={newCustomerData.city}
              placeholder="Cidade"
            />
          </Column>
        </Row>

        {errorMessage && (
          <Row className="mt-2">
            <Text size={12} className="animated fadeIn mt-1 color-danger">
              {errorMessage}
            </Text>
          </Row>
        )}

        <Row className="mt-5 align-center justify-center">
          <Button
            id="register-new-customer"
            form="add_new_customer_form"
            spinnerColor="#0E4C54"
            isRequesting={loading}
            disabled={loading}
            className="outline bg-primary flex-sm-1 align-center justify-center d-flex"
          >
            <Text className="color-primary font-bold">Cadastrar Cliente</Text>
          </Button>
        </Row>
      </Column>
    </Form>
  ) : (
    <Column className="w-100 h-100 align-center justify-center">
      <img src={IconSuccess} alt="" />
      <Text className="mt-2 color-blue-gray font-bold">
        O cliente foi salvo com sucesso
      </Text>
      <Button id="close-new-customer" onClick={onCloseSlider} className="outline mt-4" width="auto">
        <Text className="color-primary font-bold px-3">Fechar</Text>
      </Button>
    </Column>
  );
};

export default AddNewCustomer;
